import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, PlayCircle, Users, MessageCircle } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import BookSection from "@/components/site/BookSection";
import FloatingBookCTA from "@/components/site/FloatingBookCTA";

const VIDEO_ID = "YsdSBj8nylM";

const CHAPTERS = [
  { t: "00:00", s: 0, label: "Introduction — Retiring to Spain from the UK" },
  { t: "00:07", s: 7, label: "Who this is for and what you'll learn" },
  { t: "00:50", s: 50, label: "My story — Moving to Spain pre and post Brexit" },
  { t: "01:05", s: 65, label: "The biggest concerns people have" },
  { t: "02:27", s: 147, label: "The 4 stages of moving to Spain" },
  { t: "03:35", s: 215, label: "Why most people never actually make the move" },
  { t: "04:01", s: 241, label: "The critical first step — choosing your move date" },
  { t: "04:52", s: 292, label: "Financial requirements and proving income" },
  { t: "05:12", s: 312, label: "Health insurance and S1 explained" },
  { t: "06:05", s: 365, label: "Key documents (ACRO, medical, marriage cert)" },
  { t: "06:44", s: 404, label: "Visa application process step by step" },
  { t: "07:09", s: 429, label: "What happens after you arrive in Spain" },
  { t: "07:40", s: 460, label: "Tax residency rules (183 days explained)" },
  { t: "07:57", s: 477, label: "Common mistakes that can delay or ruin your application" },
  { t: "09:21", s: 561, label: "Full cost breakdown of the non-lucrative visa" },
  { t: "11:32", s: 692, label: "The 3 ways to apply (DIY vs solicitor vs consultant)" },
  { t: "13:01", s: 781, label: "Real client examples and case studies" },
  { t: "16:36", s: 996, label: "What's included in the support package" },
  { t: "18:06", s: 1086, label: "Decision point — when are you moving?" },
  { t: "18:20", s: 1100, label: "Why work with me (beyond just the visa)" },
];

const STAGES = [
  {
    n: "01",
    title: "Prep & paperwork",
    body: "The heaviest stage — gathering everything you need to prove you're eligible. Usually 2–3 months.",
  },
  {
    n: "02",
    title: "Consulate appointment",
    body: "Submit your full pack at Edinburgh, Manchester or London. Hand over your passport and wait.",
  },
  {
    n: "03",
    title: "The wait",
    body: "Anywhere from 2 weeks to 3–4 months for approval, depending on consulate and time of year.",
  },
  {
    n: "04",
    title: "Arrival & residency",
    body: "Land in Spain, register at the padrón, take fingerprints, and collect your residency card.",
  },
];

const CASES = [
  {
    name: "Julie & Richard",
    headline: "Timing the house sale with the move",
    body:
      "Worried about juggling documents that expire at different rates, plus tax timing on a UK property sale. We built a clear start-to-finish plan and adjusted as life changed.",
  },
  {
    name: "Craig",
    headline: "Edinburgh consulate, fast",
    body:
      "Diligent, methodical, and wanted 1-to-1 support at every stage. We worked through the Spanish forms with video walkthroughs and submitted at Edinburgh, confident every detail was right.",
  },
  {
    name: "Neil & Clare",
    headline: "Manchester consulate, complex finances",
    body:
      "Had sold a business and a house — needed to interpret a mix of income types in a way the consulate would accept. Worked through proofs together and submitted with confidence. Visas approved 8 weeks later.",
  },
];

const chapterUrl = (s) => `https://www.youtube.com/watch?v=${VIDEO_ID}&t=${s}s`;

export default function Masterclass() {
  const [startAt, setStartAt] = useState(0);
  const videoRef = useRef(null);

  const jumpTo = (s) => {
    setStartAt(s);
    // Scroll back up to the video and let the iframe remount with the new start time
    if (videoRef.current) {
      videoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  return (
    <main className="min-h-screen bg-[var(--cream)] text-[var(--ink)] selection:bg-[var(--terracotta)] selection:text-[var(--cream)]">
      <SiteNav />

      {/* HERO */}
      <section className="pt-28 lg:pt-32 pb-16 relative overflow-hidden editorial">
        <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-[var(--terracotta)]/10 blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[420px] h-[420px] rounded-full bg-[var(--gold)]/15 blur-3xl pointer-events-none" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--terracotta)] transition-colors"
            data-testid="masterclass-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>

          <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--cream-deep)] border border-[var(--terracotta)]/20 text-xs uppercase tracking-[0.2em] text-[var(--terracotta-deep)] font-medium">
            <PlayCircle className="w-3.5 h-3.5" /> Masterclass · ~20 minutes
          </div>

          <h1
            className="font-serif text-[2.4rem] sm:text-5xl lg:text-6xl text-[var(--navy)] mt-6 leading-[1.04]"
            data-testid="masterclass-headline"
          >
            The complete guide to <em className="text-[var(--terracotta)]">retiring to Spain</em> on the NLV.
          </h1>

          <p className="mt-7 text-lg text-[var(--ink-soft)] max-w-3xl leading-relaxed">
            Carley walks you through every stage of the Non-Lucrative Visa — from putting your
            flag in the sand and choosing your move date, all the way to picking up your residency
            card in Spain. Watch in full, or jump to the chapter you need.
          </p>
        </div>
      </section>

      {/* VIDEO */}
      <section className="pb-16" ref={videoRef}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div
            className="relative rounded-[2rem] overflow-hidden shadow-2xl bg-[var(--navy)]"
            data-testid="masterclass-video"
          >
            <div className="relative pt-[56.25%]">
              <iframe
                key={startAt}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${VIDEO_ID}?rel=0&modestbranding=1&start=${startAt}${startAt > 0 ? "&autoplay=1" : ""}`}
                title="Retiring to Spain — Full NLV Masterclass with Carley Chaplin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* CHAPTERS */}
      <section className="py-16 lg:py-20 bg-[var(--cream-deep)]/60 editorial">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
                Chapter index
              </div>
              <h2 className="font-serif text-3xl lg:text-5xl text-[var(--navy)] mt-4 leading-[1.05]">
                Jump straight to the bit <em>you need.</em>
              </h2>
            </div>
            <p className="lg:col-span-5 text-[var(--ink-soft)]">
              Click any timestamp to jump straight to that moment in the video above — the player
              will skip ahead and start playing.
            </p>
          </div>

          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1" data-testid="chapter-list">
            {CHAPTERS.map((c, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => jumpTo(c.s)}
                  className="w-full flex items-baseline gap-4 py-3 border-b border-[var(--ink)]/10 hover:bg-white/60 transition-colors px-2 -mx-2 rounded group text-left"
                  data-testid={`chapter-${i}`}
                >
                  <span className="font-mono text-xs text-[var(--terracotta)] w-12 flex-shrink-0">{c.t}</span>
                  <span className="text-[0.95rem] text-[var(--ink)] group-hover:text-[var(--terracotta-deep)] transition-colors leading-snug">
                    {c.label}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CALENDLY EMBED */}
      <BookSection
        id="book"
        eyebrow="Ready when you are"
        headline={
          <>
            Book your eligibility
            <br />
            <em>&amp; next-steps call.</em>
          </>
        }
        lede="The same call Carley mentions at the end of the masterclass. A relaxed, honest conversation to check eligibility and map your timeline."
      />

      {/* 4 STAGES */}
      <section className="py-24 lg:py-28 editorial">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
              The 4 stages
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl text-[var(--navy)] mt-4 leading-[1.05]">
              From <em>"someday"</em> to your residency card in hand.
            </h2>
            <p className="mt-5 text-[var(--ink-soft)] text-lg">
              Most people don't realise the consulate appointment isn't the finish line — there's
              a whole second act once you arrive in Spain.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STAGES.map((st, i) => (
              <div
                key={i}
                className="soft-card rounded-2xl p-7 hover:bg-white transition-colors duration-500"
                data-testid={`stage-${i}`}
              >
                <div className="font-serif text-3xl text-[var(--terracotta)]">{st.n}</div>
                <h3 className="mt-3 font-serif text-xl text-[var(--navy)]">{st.title}</h3>
                <p className="mt-2 text-sm text-[var(--ink-soft)] leading-relaxed">{st.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FLAG IN THE SAND */}
      <section className="py-20 lg:py-24 bg-[var(--navy)] text-[var(--cream)] relative overflow-hidden grain editorial">
        <div className="absolute inset-0 opacity-10 pointer-events-none bg-gradient-to-br from-[var(--terracotta)] to-transparent" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.35em] text-[var(--gold)] font-medium">
            The critical first step
          </div>
          <h2 className="font-serif text-3xl lg:text-5xl mt-5 leading-[1.05]">
            Put your <em className="text-[var(--gold)]">flag in the sand.</em>
          </h2>
          <p className="mt-6 text-lg text-[var(--cream)]/90 leading-relaxed">
            "Until you say <em className="text-[var(--gold)] not-italic font-medium">'I am going
            to move on X date'</em> — it simply won't happen. It will keep drifting." Pick the
            date. Work backwards. Everything else falls into place.
          </p>
        </div>
      </section>

      {/* REAL CLIENT EXAMPLES */}
      <section className="py-24 lg:py-28 editorial">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium flex items-center gap-2">
              <Users className="w-4 h-4" /> Real client examples
            </div>
            <h2 className="font-serif text-3xl lg:text-5xl text-[var(--navy)] mt-4 leading-[1.05]">
              Three different journeys. <em>Same destination.</em>
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {CASES.map((c, i) => (
              <article
                key={i}
                className="rounded-2xl p-7 bg-white border border-black/5 hover:border-[var(--terracotta)]/30 transition-colors"
                data-testid={`case-${i}`}
              >
                <div className="text-xs uppercase tracking-[0.25em] text-[var(--terracotta)] font-medium">{c.name}</div>
                <h3 className="mt-3 font-serif text-2xl text-[var(--navy)] leading-tight">{c.headline}</h3>
                <p className="mt-4 text-[var(--ink-soft)] text-[0.95rem] leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* DECISION POINT */}
      <section className="py-20 lg:py-24 bg-[var(--cream-deep)]/60 editorial">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-[var(--terracotta)] font-medium">
            Decision point
          </div>
          <h2 className="font-serif text-3xl lg:text-5xl text-[var(--navy)] mt-5 leading-[1.05]">
            So… when are you <em>moving to Spain?</em>
          </h2>
          <p className="mt-6 text-[var(--ink-soft)] text-lg">
            Carley only takes on a small number of clients at a time. If retiring to Spain in the
            next 6–12 months is on the cards, the next step is a conversation.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <a
              href="#book"
              className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[var(--terracotta)] text-[var(--cream)] font-medium"
              data-testid="masterclass-cta-book"
            >
              Book your call <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="https://m.me/gettospain"
              target="_blank"
              rel="noreferrer"
              className="pill inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[var(--navy)]/25 text-[var(--navy)] hover:bg-[var(--navy)] hover:text-[var(--cream)] font-medium"
              data-testid="masterclass-cta-message"
            >
              <MessageCircle className="w-5 h-5" /> Message me on Facebook
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
      <FloatingBookCTA />
    </main>
  );
}
